// Состояние формы карточки: значения, изменённое, ошибки, черновик, подстановка, сохранение.
//
// ЗАЧЕМ ОТДЕЛЬНЫМ ХУКОМ. Всё перечисленное связано между собой: правка поля меняет
// изменённое, пересчитывает ошибки, роняет пометку «подставлено», перезаписывает черновик
// и может подтянуть значения из связанной карточки. Если каждая карточка соберёт этот узел
// сама, разойдутся не подписи, а поведение: где-то черновик переживёт сохранение, где-то
// подстановка затрёт набранное руками.
//
// ЧТО ЗДЕСЬ НАРОЧНО НЕ ДЕЛАЕТСЯ. Хук не рисует, не знает про вкладки и не ходит за журналом
// и лентой: они читаются один раз при открытии карточки и от правки полей не зависят.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { забытьЧерновик, прочитатьЧерновик, сохранитьЧерновик } from './draft'
import { ОтказЗаписи, РевизияРазошлась } from './source'
import type { ИсточникКарточек } from './source'
import { изменённые } from './history'
import { поУмолчанию, пусто, равны, этоЯрлык } from './values'
import { незаполненныеОбязательные, проверитьКарточку } from './validate'
import type {
  Значение,
  Значения,
  ОписаниеКарточки,
  Поле,
  Состояние,
} from './types'

/**
 * Ключ, под которым состояние записи попадает в значения.
 *
 * Состояние — не поле карточки: его не набирают, его меняют кнопкой. Но условная
 * обязательность без него не выражается («номер обязателен при выпуске»), поэтому
 * в проверки и в описание оно приезжает под этим ключом и только для чтения.
 */
// Ключ ОДИН на форму и на описания: описание читает состояние из значений под
// 'doc_state' (см. specs/obshchee.ts), и если форма положит его под другим именем,
// условная обязательность номера тихо перестанет срабатывать — проверено: без
// 'doc_state' в значениях пустой номер у выпущенного акта ошибкой быть перестаёт.
export const КЛЮЧ_СОСТОЯНИЯ = 'doc_state'

export interface ХодСохранения {
  идёт: boolean
  ошибка: string | null
  /** Когда сохранено в последний раз, ISO. Показывается в шапке. */
  когда: string | null
}

export interface Форма {
  значения: Значения
  /** Значения вместе с состоянием — их и надо отдавать в описание и проверки. */
  сПометкой: Значения
  исходные: Значения
  изменённые: Set<string>
  ошибки: Record<string, string>
  /** Поля, которых человек уже касался: до этого краснеть нечему. */
  тронутые: Set<string>
  /** Код поля → откуда подставлено. Пометка живёт до первой ручной правки поля. */
  подставленные: Record<string, string>
  незаполненные: Поле[]
  черновикОт: string | null
  состояние: Состояние
  ревизия: number
  сохранение: ХодСохранения
  /** Есть ли несохранённые правки. */
  грязная: boolean
  установить: (код: string, v: Значение) => void
  отменитьПравки: () => void
  сброситьЧерновик: () => void
  сохранить: (новоеСостояние?: Состояние) => Promise<boolean>
  /** Показать ошибки целиком — нажали «Сохранить» при незаполненном обязательном. */
  показатьВсеОшибки: () => void
}

/** Пустая карточка: у каждого поля значение по умолчанию своего вида. */
export function пустыеЗначения(описание: ОписаниеКарточки): Значения {
  const з: Значения = {}
  for (const п of описание.поля) з[п.код] = поУмолчанию(п)
  return з
}

interface Вход {
  описание: ОписаниеКарточки
  id: string
  исходные: Значения
  ревизия: number
  состояние: Состояние
  источник: ИсточникКарточек
  /** Кто правит. Нужен журналу; в форму приезжает из /api/me. */
  кто?: string
  /** Черновик выключается там, где карточка открыта только на чтение. */
  безЧерновика?: boolean
}

export function useCardForm({
  описание,
  id,
  исходные,
  ревизия: ревизияИсходная,
  состояние: состояниеИсходное,
  источник,
  безЧерновика,
}: Вход): Форма {
  const [значения, setЗначения] = useState<Значения>(исходные)
  const [база, setБаза] = useState<Значения>(исходные)
  const [ревизия, setРевизия] = useState(ревизияИсходная)
  const [состояние, setСостояние] = useState<Состояние>(состояниеИсходное)
  const [тронутые, setТронутые] = useState<Set<string>>(() => new Set())
  const [подставленные, setПодставленные] = useState<Record<string, string>>({})
  const [черновикОт, setЧерновикОт] = useState<string | null>(null)
  const [сохранение, setСохранение] = useState<ХодСохранения>({
    идёт: false, ошибка: null, когда: null,
  })
  // Отказы СЛОЯ ЗАПИСИ по полям: {код: что не так его словами}. Отдельно от клиентских
  // проверок: те пересчитываются на каждый ввод, а отказ сервера — факт о конкретной
  // попытке сохранить, и живёт он до правки поля или до следующего сохранения.
  const [ошибкиЗаписи, setОшибкиЗаписи] = useState<Record<string, string>>({})

  const живой = useRef(true)
  useEffect(() => () => { живой.current = false }, [])

  // ── восстановление черновика ──
  // Один раз на открытие карточки. Если черновик совпадает с записью — молчим: сообщать
  // «восстановлен черновик», когда восстанавливать нечего, значит приучить его не читать.
  const ключЗаписи = `${описание.сущность}:${id}`
  const восстановлен = useRef('')
  useEffect(() => {
    if (безЧерновика) return
    if (восстановлен.current === ключЗаписи) return
    восстановлен.current = ключЗаписи
    const ч = прочитатьЧерновик(описание.сущность, id)
    if (!ч) return
    const есть = описание.поля.some((п) => !равны(ч.значения[п.код], исходные[п.код]))
    if (!есть) {
      забытьЧерновик(описание.сущность, id)
      return
    }
    setЗначения({ ...исходные, ...ч.значения })
    setЧерновикОт(ч.сохранён)
  }, [ключЗаписи, безЧерновика, описание, id, исходные])

  const сПометкой = useMemo(
    () => ({ ...значения, [КЛЮЧ_СОСТОЯНИЯ]: состояние }),
    [значения, состояние],
  )

  const ошибки = useMemo(
    // Серверные отказы и клиентские проверки — одним словарём: полю всё равно, кто его
    // отверг, а человеку нужен весь список сразу. При совпадении ключа клиентская проверка
    // главнее: она живая и пересчитана по текущему значению, а серверная — о прошлой попытке.
    () => ({ ...ошибкиЗаписи, ...проверитьКарточку(описание.поля, сПометкой) }),
    [описание.поля, сПометкой, ошибкиЗаписи],
  )

  const набор = useMemo(
    () => изменённые(описание.поля, база, значения),
    [описание.поля, база, значения],
  )

  const незаполненные = useMemo(
    () => незаполненныеОбязательные(описание.поля, сПометкой),
    [описание.поля, сПометкой],
  )

  // ── подстановка из связанной карточки ──
  // Работает в одну сторону и только вниз по зависимости: выбрали компанию — подтянулись
  // её условия. Обратно (правка условий меняет компанию) не бывает и быть не должно.
  const подтянуть = useCallback(async (кодСвязи: string, следом: Значения) => {
    const правила = описание.поля.filter((п) => п.подстановка?.из === кодСвязи)
    if (!правила.length) return
    const я = следом[кодСвязи]
    if (!этоЯрлык(я) || !я.id) return
    const запись = await источник.запись(я.сущность, я.id)
    if (!запись || !живой.current) return
    const добавка: Значения = {}
    const отметки: Record<string, string> = {}
    for (const п of правила) {
      const пр = п.подстановка
      if (!пр) continue
      const толькоВПустое = пр.толькоВПустое !== false
      if (толькоВПустое && !пусто(следом[п.код])) continue
      const v = запись.значения[пр.взять]
      if (пусто(v)) continue
      добавка[п.код] = v
      отметки[п.код] = я.название
    }
    if (!Object.keys(добавка).length) return
    setЗначения((прежние) => {
      const новые = { ...прежние, ...добавка }
      if (!безЧерновика) сохранитьЧерновик(описание.сущность, id, новые, ревизия)
      return новые
    })
    setПодставленные((прежние) => ({ ...прежние, ...отметки }))
  }, [описание.поля, источник, безЧерновика, описание.сущность, id, ревизия])

  const установить = useCallback((код: string, v: Значение) => {
    setЗначения((прежние) => {
      const новые = { ...прежние, [код]: v }
      if (!безЧерновика) сохранитьЧерновик(описание.сущность, id, новые, ревизия)
      void подтянуть(код, новые)
      return новые
    })
    setТронутые((прежние) => (прежние.has(код) ? прежние : new Set(прежние).add(код)))
    // Правка поля гасит серверный отказ ПО ЭТОМУ полю: человек уже отвечает на него,
    // и красное «ИНН — десять цифр» поверх исправляемого значения только мешало бы.
    setОшибкиЗаписи((прежние) => {
      if (!(код in прежние)) return прежние
      const копия = { ...прежние }
      delete копия[код]
      return копия
    })
    // Правка руками снимает пометку «подставлено»: значение больше не заготовка.
    setПодставленные((прежние) => {
      if (!(код in прежние)) return прежние
      const копия = { ...прежние }
      delete копия[код]
      return копия
    })
  }, [безЧерновика, описание.сущность, id, ревизия, подтянуть])

  const отменитьПравки = useCallback(() => {
    setЗначения(база)
    setПодставленные({})
    setОшибкиЗаписи({})
    setЧерновикОт(null)
    забытьЧерновик(описание.сущность, id)
  }, [база, описание.сущность, id])

  const сброситьЧерновик = useCallback(() => {
    забытьЧерновик(описание.сущность, id)
    setЧерновикОт(null)
    setЗначения(база)
    setПодставленные({})
    setОшибкиЗаписи({})
  }, [база, описание.сущность, id])

  const показатьВсеОшибки = useCallback(() => {
    setТронутые(new Set(описание.поля.map((п) => п.код)))
  }, [описание.поля])

  const сохранить = useCallback(async (новоеСостояние?: Состояние) => {
    const проверочные = новоеСостояние
      ? { ...значения, [КЛЮЧ_СОСТОЯНИЯ]: новоеСостояние }
      : сПометкой
    const плохие = проверитьКарточку(описание.поля, проверочные)
    if (Object.keys(плохие).length) {
      setТронутые(new Set(описание.поля.map((п) => п.код)))
      setСохранение({ идёт: false, ошибка: null, когда: сохранение.когда })
      return false
    }
    setСохранение({ идёт: true, ошибка: null, когда: сохранение.когда })
    try {
      // Существующей записи уходят только ИЗМЕНЁННЫЕ поля, новой — карточка целиком.
      // Это не экономия байтов: часть полей карточка показывает, но не правит (адрес из
      // реквизитов, место сотрудника — они живут в соседних таблицах), и слой записи
      // честно откажет, получив их. Нетронутые поля — не воля человека, слать их незачем.
      const посылаемые = id === 'new'
        ? значения
        : Object.fromEntries([...набор].map((код) => [код, значения[код]]))
      const итог = await источник.сохранить(
        описание.сущность, id, посылаемые, ревизия, новоеСостояние,
      )
      if (!живой.current) return true
      // Ответ источника главнее набранного, но поверх ПОЛНОЙ карточки: транспорт с частичной
      // отправкой может вернуть эхо только посланных полей, и без слияния остальные поля
      // формы стёрлись бы на пустые.
      const слитые = { ...значения, ...итог.значения }
      setБаза(слитые)
      setЗначения(слитые)
      setРевизия(итог.ревизия)
      setСостояние(итог.состояние)
      setПодставленные({})
      setОшибкиЗаписи({})
      setЧерновикОт(null)
      забытьЧерновик(описание.сущность, id)
      setСохранение({ идёт: false, ошибка: null, когда: new Date().toISOString() })
      return true
    } catch (е) {
      if (!живой.current) return false
      if (е instanceof ОтказЗаписи) {
        // Отказ слоя записи: причины по полям встают К ПОЛЯМ (его же словами), общий текст
        // — в шапку. Поля помечаются тронутыми, иначе отказ по нетронутому полю не виден.
        setОшибкиЗаписи(е.поля)
        setТронутые(new Set(описание.поля.map((п) => п.код)))
        setСохранение({ идёт: false, ошибка: е.message, когда: сохранение.когда })
        return false
      }
      const текст = е instanceof РевизияРазошлась
        ? `${е.message.replace(/\.?\s*$/, '.')} Набранное вами останется в черновике.`
        : `Сохранить не удалось: ${(е as Error).message}`
      setСохранение({ идёт: false, ошибка: текст, когда: сохранение.когда })
      return false
    }
  }, [значения, набор, сПометкой, описание.поля, описание.сущность, id, ревизия, источник,
      сохранение.когда])

  return {
    значения,
    сПометкой,
    исходные: база,
    изменённые: набор,
    ошибки,
    тронутые,
    подставленные,
    незаполненные,
    черновикОт,
    состояние,
    ревизия,
    сохранение,
    грязная: набор.size > 0,
    установить,
    отменитьПравки,
    сброситьЧерновик,
    сохранить,
    показатьВсеОшибки,
  }
}
